import { URLSearchParams } from "url";
import { PushContextType } from "../PushContextType";
import { ApplePushContext } from "../interfaces/ApplePushContext";
import { Application } from "../interfaces/Application.interface";
import { ApplicationRegistrationStats } from "../interfaces/ApplicationRegistrationStats";
import { BrowserPushContext } from "../interfaces/BrowserPushContext";
import { Audience, Campaign, CampaignDetail, CreateAudience } from "../interfaces/Campaign.interface";
import { CampaignEvent } from "../interfaces/CampaignEvent.interface";
import { DevicesResult } from "../interfaces/DevicesResult.interface";
import { FCMPushContext } from "../interfaces/FCMPushContext";
import { IDevice } from "../interfaces/IDevice";
import {
  AndroidApplication, ApplicationDetail,
  iOSApplication,
  OrgDetail,
  SearchConfiguration,
  WebApplication
} from "../interfaces/OrgDetail.interface";
import { Organisation } from "../interfaces/Organisation.interface";
import { PaginationOptions, PaginationResult } from "../interfaces/PaginationOptions.interface";
import { CampaignFilterOptions } from "../interfaces/FilterCampaignTypes";
import { Tag } from "../interfaces/Tag.interface";
import { TargetingPushContext } from "../interfaces/TargetingPushContext";
import { TimeSeriesEvent } from "../interfaces/TimeSeriesEvent.interface";
import { CampaignMetrics } from "../interfaces/campaignmetrics.interface";
import { APIValidationError } from "../utils/api.validation.utils";
import { getBase64 } from "../utils/file.utils";

export class APIService {

  host: string = "/api";//http://localhost:3020";//"http://localhost:3020/api" //'./api'

  authToken: string| null = null;

  setAuthToken(token:string|null) {
    console.log("api:setAuthToken", token);
    this.authToken = token;
  }

  mapMMContextToPushContext = async(mmContext:string|null): Promise<string|null> => {
    if(mmContext)
    {
      return await fetch(`${this.host}/v1/auth/map?mmContext=${encodeURIComponent(mmContext)}`,{
        method: "POST",
      }).then(x=>x.json()).then(x=>x.data).catch(err=>null);
    }
    return null;
  }
  getOrgApplications = async (orgId: string): Promise<Application[]> => {
    let headers:HeadersInit = {};
    console.log("getOrgApplications", this.authToken)
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }
   
    return await fetch(`${this.host}/v1/config/org/${orgId}/apps`,{
      headers: headers
    }).then(x => x.json()).then(x => x.data as Application[]).catch(err => []);
  }

  getCampaignStats = async (campaignId: string): Promise<CampaignMetrics | null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/config/campaign/${campaignId}/report`,{
      headers: headers
    }).then(x => x.json()).then(x => x.data as CampaignMetrics).catch(err => null);
  }

  getOrganisations = async (): Promise<Organisation[] | null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/config/organisations`,{
      headers: headers
    }).then(x => x.json()).then(x => x.data as Organisation[]).catch(err => null)
  }

  getCampaigns = async (applicationId: string,options?: CampaignFilterOptions): Promise<{ results: CampaignDetail[], total: number } | null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }
    

    let queryParams:any = {
      ...options,
    };
    
    return await fetch(`${this.host}/v1/config/app/${applicationId}/campaigns?${Object.entries<string>(queryParams).map(x=>`${x[0]}=${encodeURIComponent(x[1])}`).join('&')}`,{
      headers: headers
    }).then(x => x.json()).then(x => x.data as { results: CampaignDetail[], total: number}).catch(err => null);
  }

  createCampaign = async (applicationId: string, campaign: Campaign): Promise<CampaignDetail> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }
    return await fetch(`${this.host}/v1/config/app/${applicationId}/campaign`, {
      method: "POST",
      headers: {
        "content-type": "application/json; charset=utf-8",
        ...headers
      },
      body: JSON.stringify(campaign)
    }).then(async res=>{
      let result:any = {};
      try {
        result = await res.json();
      } catch {
        result = {};
      }
      console.log("createCampaignapi:", result);

      // Non-2xx: surface server message/context to caller
      const defaultMessage = res.status === 413
        ? "Request too large. Please reduce the image size."
        : `HTTP ${res.status}: ${res.statusText || "Request failed"}`;

      const message = typeof result?.message === "string"
        ? result.message
        : defaultMessage;

      if (!res.ok) {
        throw new APIValidationError({
          message,
          context: result?.context ?? [],
          error: result?.error ?? [{ status: res.status }]
        });
      }

      if(result?.data)
      {
        return result.data as CampaignDetail
      } else {
        throw new APIValidationError({
          message,
          context: result?.context ?? [],
          error: result?.error ?? []
        });
      }
    });
    
    // let result = await data.json();

    // console.log("campaign result", result);
    // if(result.data)
    // {
    //     return result as CampaignDetail;
    // } else {
    //     throw new APIValidationError(result);
    //     return result;
    // };
  }

  createAudience = async (applicationId: string, audience: CreateAudience) => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    audience.applicationId = applicationId;

    return await fetch(`${this.host}/v1/config/app/${applicationId}/audience`, {
      method: "POST",
      headers: {
        "content-type": "application/json; charset=utf-8",
        ...headers
      },
      body: JSON.stringify(audience)
    }).then(async response => {
      const data = await response.json();
      if (!response.ok) {
        // Create an error object that includes the response data for better error handling
        const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
        (error as any).response = { data, status: response.status };
        throw error;
      }
      return data;
    })
  }

  getAudiences = async (applicationId: string): Promise<Audience[] | null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/config/app/${applicationId}/audiences`,{
      headers: headers
    }).then(x => x.json()).then(x => x.data as Audience[]).catch(err => null);
  }

  getTagsForApp = async (applicationId: string): Promise<Tag[] | null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }
    return await fetch(`${this.host}/v1/config/app/${applicationId}/tags`,{
      headers: headers
    }).then(x => x.json()).then(x => x.data as Tag[]).catch(err => null);
  }

  getCampaignDetail = async (applicationId: string, campaignId: string): Promise<CampaignDetail | null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }
    return await fetch(`${this.host}/v1/config//app/${applicationId}/campaign/${campaignId}`,{
      headers: headers
    }).then(x => x.json()).then(x => x.data as CampaignDetail).catch(err => null);
  }

  getCampaignLogs = async (applicationId: string, campaignId: string, options: PaginationOptions): Promise<PaginationResult<CampaignEvent>|null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/config/app/${applicationId}/campaign/${campaignId}/logs?${Object.entries<any>(options).map(x=>`${x[0]}=${encodeURIComponent(x[1])}`).join('&')}`,{
      headers: headers
    }).then(x=>x.json()).then(x=>x.data as PaginationResult<CampaignEvent>).catch(err=>null);
  }

  getCampaignTimeSeries = async (applicationId:string, campaignId:string, cumulative: boolean): Promise<TimeSeriesEvent[]|null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    let queryParams:any = {}
    if(cumulative)
    {
      queryParams.cumulative = cumulative ? 1 :0
    }
    return await fetch(`${this.host}/v1/config/app/${applicationId}/campaign/${campaignId}/timeseries?${Object.entries<any>(queryParams).map(x=>`${x[0]}=${encodeURIComponent(x[1])}>`).join('&')}`,{
      headers: headers
    }).then(x=>x.json()).then(x=>x.data as TimeSeriesEvent[]).catch(err=>null);
  }

  cloneCampaign = async(applicationId:string, campaign:Campaign) : Promise<Campaign|null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }
    return await fetch(`${this.host}/v1/config/app/${applicationId}/campaign/${campaign.id}/clone`, {
      method: "POST",
      headers: headers
    }).then(x=>x.json()).then(x=>x.data as Campaign).catch(err=>null);
  }

  cancelCampaign = async (applicationId: string, campaignId: string): Promise<CampaignDetail> => {
    let headers: HeadersInit = {};
    if (this.authToken) {
      headers.authorization = `Bearer ${this.authToken}`;
    }

    return await fetch(`${this.host}/v1/config/app/${applicationId}/campaign/${campaignId}/cancel`, {
      method: "POST",
      headers: {
        ...headers
      }
    }).then(async res => {
      let result: any = {};
      try {
        result = await res.json();
      } catch {
        result = {};
      }

      const defaultMessage = `HTTP ${res.status}: ${res.statusText || "Request failed"}`;
      const message = typeof result?.message === "string" ? result.message : defaultMessage;

      if (!res.ok) {
        throw new APIValidationError({
          message,
          context: result?.context ?? [],
          error: result?.error ?? [{ status: res.status }]
        });
      }

      if (result?.data) {
        return result.data as CampaignDetail;
      }

      throw new APIValidationError({
        message,
        context: result?.context ?? [],
        error: result?.error ?? []
      });
    });
  }

  getDevices = async (applicationId:string, options: PaginationOptions) : Promise<DevicesResult|null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    let queryParams:any = {};
    queryParams.limit = options.limit;
    queryParams.offset = options.offset;

    return await fetch(`${this.host}/v1/config/app/${applicationId}/devices?${Object.entries<any>(queryParams).map(x=>`${x[0]}=${encodeURIComponent(x[1])}`).join('&')}`, {
      method: "GET",
      headers: headers
    }).then(x=>x.json()).then(x=>x.data as DevicesResult).catch(err=>null);
  }

  getApplicationRegistrationStats = async (applicationId: string): Promise<ApplicationRegistrationStats|null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/config/app/${applicationId}/stats`, {
      method: "GET",
      headers: headers
    }).then(x=>x.json()).then(x=>{
      
      x.data.summary = x.data.summary.map((x:{ count: number, date: string})=>{
        return {
          count: x.count,
          date: new Date(x.date)
        };
      })
      x.data.cumulativeSummary = x.data.cumulativeSummary.map((x:{ count: number, date: string})=>{
        return {
          count: x.count,
          date: new Date(x.date)
        };
      })
      return x.data as ApplicationRegistrationStats

    }).catch(err=>null);
  }

  login = async (username: string, password: string): Promise<string|null> => {
    return await fetch(`${this.host}/v1/auth/logon`, {
      method: "POST",
      headers: {
        "content-type": "application/json"
      },
      body: JSON.stringify({
        username: username,
        password: password
      })
    }).then(x=>x.json()).then(x=>x.data).catch(err=>null);
  }

  allOrganisations = async (options: PaginationOptions):Promise<PaginationResult<Organisation>|null> => {

    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/orgs?limit=${options.limit}&offset=${options.offset}`, {
      method: "GET",
      headers: {
        ...headers,
        "content-type": "application/json"
      }
    }).then(x=>x.json()).then(x=>x.data as PaginationResult<Organisation>).catch(err=>null)
  }

  createOrganisation = async (org:Organisation) => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org`, {
      method: "POST",
      headers: {
        ...headers,
        "content-type": "application/json"
      },
      body: JSON.stringify(org)
    }).then(x=>x.json()).then(x=>x.data as Organisation).catch(err=>null)
  }

  getOrgDetail = async (orgId:string):Promise<OrgDetail|null> => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org/${orgId}`, {
      method: "GET",
      headers: {
        ...headers,
      },

    }).then(x=>x.json()).then(x=>x.data as OrgDetail).catch(err=>null)
  }

  saveSearchConfiguration = async (searchConfig: SearchConfiguration, applicationId:string, orgId:string) => {
    let headers:HeadersInit = {"content-type": "application/json"};
    if(this.authToken) {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org/${orgId}/app/${applicationId}/searchConfig`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(searchConfig),
    }).then(r => {
      if (!r.ok) {
        throw `saveSearchConfiguration:error - http ${r.status}`
      }

      return r.json()
    }).then(r => r.data as SearchConfiguration)
  }

  saveWebApplication = async (webApp: WebApplication, applicationId:string, orgId:string) => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org/${orgId}/app/${applicationId}/web`, {
      method: "POST",
      headers: {
        ...headers,
        "content-type": "application/json"
      },
      body: JSON.stringify(webApp),

    }).then(x=>x.json()).then(x=>x.data as WebApplication).catch(err=>{
      console.log("saveWebApplication:err", err);
      
      return null;
    })
  }

  saveAndroidApplication = async (androidApp: AndroidApplication, applicationId:string, orgId:string) => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org/${orgId}/app/${applicationId}/android`, {
      method: "POST",
      headers: {
        ...headers,
        "content-type": "application/json"
      },
      body: JSON.stringify(androidApp),

    }).then(x=>x.json()).then(x=>x.data as AndroidApplication).catch(err=>{
      console.log("saveAndroidApplication:err", err);
      
      return null;
    })
  }

  saveIOSApplication = async (iosApp: iOSApplication, applicationId:string, orgId:string) => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org/${orgId}/app/${applicationId}/ios`, {
      method: "POST",
      headers: {
        ...headers,
        "content-type": "application/json"
      },
      body: JSON.stringify(iosApp),

    }).then(x=>x.json()).then(x=>x.data as AndroidApplication).catch(err=>{
      console.log("saveIOSApplication:err", err);
      
      return null;
    })
  }

  createAPIKey = async (name: string, applicationId:string, orgId:string) => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org/${orgId}/app/${applicationId}/apiKey`, {
      method: "POST",
      headers: {
        ...headers,
        "content-type": "application/json"
      },
      body: JSON.stringify({
        name: name
      }),

    }).then(x=>x.json()).then(x=>x.data as boolean).catch(err=>{
      console.log("createAPIKey:err", err);
      
      return null;
    })
  }

  updateAPIStatus = async (enabled:boolean, apiKeyId:string, applicationId:string, orgId:string) => {
    let headers:HeadersInit = {};
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/admin/org/${orgId}/app/${applicationId}/apiKey/${apiKeyId}/status`, {
      method: "POST",
      headers: {
        ...headers,
        "content-type": "application/json"
      },
      body: JSON.stringify({
        enabled: enabled
      }),

    }).then(x=>x.json()).then(x=>x.data as boolean).catch(err=>{
      console.log("updateAPIStatus:err", err);
      
      return null;
    })
  }

  getImageBase64 = async (campaign:Campaign):Promise<string|null> => {
    return await fetch(`${this.host}/v1/app/${campaign.applicationId}/campaign/${campaign.id}/image`)
    .then((response) => response.status == 200 ? response.blob() : null)
    .then((blob) => {
      if(blob == null)
      {
        return null;
      } else {
        return new Promise<string>((resolve, reject)=>{
          const reader = new FileReader();
          reader.readAsDataURL(blob);
          reader.onloadend = () => {
            const base64String = reader.result;
            if(base64String)
              resolve(base64String?.toString());
            else 
              reject(null);
          };
        });
      }
    });
  }

  downloadAudienceFileAsBase64 = async (applicationId:string, audienceId:string, fileId:string, fileName:string): Promise<string> => {
    
    let headers:HeadersInit = {
      "content-type": "application/json; charset=utf-8",
    };
    if(this.authToken)
    {
      headers.authorization = `Bearer ${this.authToken}`
    }

    return await fetch(`${this.host}/v1/config/app/${applicationId}/audience/${audienceId}/file/${fileId}`, {
      method: 'GET',
      headers:headers
    })
    .then((res) => {
        return res.blob();
    })
    .then((blob) => {
      return getBase64(blob);
    })
    .catch((err) => {
        return Promise.reject({ Error: 'Something Went Wrong', err });
    })
}

  downloadAudienceFile = async (applicationId:string, audienceId:string, fileId:string, fileName:string): Promise<void> => {
    
      let headers:HeadersInit = {
        "content-type": "application/json; charset=utf-8",
      };
      if(this.authToken)
      {
        headers.authorization = `Bearer ${this.authToken}`
      }

      fetch(`${this.host}/v1/config/app/${applicationId}/audience/${audienceId}/file/${fileId}`, {
        method: 'GET',
        headers:headers
      })
      .then((res) => {
          return res.blob();
      })
      .then((blob) => {
          const href = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = href;
          console.log("download", fileName);
          link.setAttribute('download', fileName); //or any other extension
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      })
      .catch((err) => {
          return Promise.reject({ Error: 'Something Went Wrong', err });
      })
  }

  public async setDeviceTag(applicationId: string,deviceId: string, tagName: string, value: boolean) {
    const url = `/v1/config/app/${applicationId}/device/${deviceId}/tags/${tagName}/type/bool`;
    await this.post(url, { value });
  }

  private async post(url: string, body: any): Promise<any> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };
    if (this.authToken) {
      headers.Authorization = `Bearer ${this.authToken}`;
    }

    const response = await fetch(`${this.host}${url}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

}